import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

interface ContactSubmission {
  _id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  isRead: boolean;
  isReplied: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function ContactTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ContactSubmission | null>(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contact');
      setContacts(response.data.data || []);
    } catch (error) {
      console.error('Error loading contact submissions:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      setMarking(true);
      await api.patch(`/contact/${id}/read`);
      loadContacts();
      if (selected?._id === id) setSelected((prev) => prev ? { ...prev, isRead: true } : null);
    } catch (error) {
      console.error('Error marking as read:', error);
    } finally {
      setMarking(false);
    }
  };

  const markAsReplied = async (id: string) => {
    try {
      setMarking(true);
      await api.patch(`/contact/${id}/replied`);
      loadContacts();
      if (selected?._id === id) setSelected((prev) => prev ? { ...prev, isReplied: true } : null);
    } catch (error) {
      console.error('Error marking as replied:', error);
    } finally {
      setMarking(false);
    }
  };

  const formatDate = (s: string) => {
    try {
      return new Date(s).toLocaleString();
    } catch {
      return s;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('contact.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className={`px-4 py-3 border-b border-gray-200 flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <ChatBubbleLeftRightIcon className="w-6 h-6 text-primary-orange" />
          <h2 className="text-lg font-semibold text-gray-800">{t('contact.submissions')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('contact.date')}</th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('contact.name')}</th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('contact.email')}</th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('contact.subject')}</th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('contact.status')}</th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('contact.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    {t('contact.noSubmissions')}
                  </td>
                </tr>
              ) : (
                contacts.map((c) => (
                  <tr
                    key={c._id}
                    className={selected?._id === c._id ? 'bg-orange-50' : c.isRead ? '' : 'bg-blue-50/50'}
                  >
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(c.createdAt)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{c.subject}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${c.isReplied ? 'bg-green-100 text-green-800' : c.isRead ? 'bg-gray-100 text-gray-800' : 'bg-amber-100 text-amber-800'}`}>
                        {c.isReplied ? t('contact.replied') : c.isRead ? t('contact.read') : t('contact.new')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                    <div className={`flex flex-wrap items-center gap-3 ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                      <button
                        type="button"
                        onClick={() => setSelected(selected?._id === c._id ? null : c)}
                        className="text-primary-orange hover:underline text-sm font-medium"
                      >
                        {selected?._id === c._id ? t('contact.hide') : t('contact.view')}
                      </button>
                      {!c.isRead && (
                        <button
                          type="button"
                          disabled={marking}
                          onClick={() => markAsRead(c._id)}
                          className="text-gray-600 hover:underline text-sm disabled:opacity-50"
                        >
                          {t('contact.markAsRead')}
                        </button>
                      )}
                      {!c.isReplied && (
                        <button
                          type="button"
                          disabled={marking}
                          onClick={() => markAsReplied(c._id)}
                          className="text-gray-600 hover:underline text-sm disabled:opacity-50"
                        >
                          {t('contact.markAsReplied')}
                        </button>
                      )}
                    </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className={`bg-white rounded-lg shadow-md p-4 border border-gray-200 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
          <div className={`flex justify-between items-start mb-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-lg font-semibold text-gray-800">{selected.subject}</h3>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <span className="text-xl">&times;</span>
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-1"><strong>{t('contact.from')}:</strong> {selected.name} &lt;{selected.email}&gt;</p>
          <p className="text-sm text-gray-500 mb-3">{formatDate(selected.createdAt)}</p>
          <div className="p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap text-gray-800">
            {selected.message}
          </div>
          <div className={`mt-3 flex flex-wrap gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
            {!selected.isRead && (
              <button
                type="button"
                disabled={marking}
                onClick={() => markAsRead(selected._id)}
                className="px-3 py-1.5 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                {t('contact.markAsRead')}
              </button>
            )}
            {!selected.isReplied && (
              <button
                type="button"
                disabled={marking}
                onClick={() => markAsReplied(selected._id)}
                className="px-3 py-1.5 text-sm bg-primary-orange text-white rounded hover:bg-orange-600 disabled:opacity-50"
              >
                {t('contact.markAsReplied')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
